import { XIcon } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { AddSessionFormData } from "../types/add-session-schema";
import { UserOption } from "../types/user-option";

function getName(attendee: UserOption) {
  return attendee.isExternal
    ? attendee.email
    : `${attendee.firstName} ${attendee.lastName}`;
}

function getMonogram(attendee: UserOption) {
  return attendee.isExternal
    ? attendee.email[0]
    : `${attendee.firstName[0]}${attendee.lastName[0]}`;
}

export function AttendeesListElement() {
  const form = useFormContext<AddSessionFormData>();
  const attendees = useFieldArray({
    control: form.control,
    name: "guests",
  });

  if (attendees.fields.length === 0) {
    return null;
  }
  return (
    <>
      <div className="flex items-center justify-between rounded-t-md border border-b-0 border-border px-3 py-2">
        <p className="text-xs font-medium text-muted">Attendees</p>
        <p className="mr-7 text-xs font-medium text-muted">Moderator</p>
      </div>

      <ScrollArea
        type="always"
        className={cn("rounded-b-md border border-border", {
          "h-[427px]": attendees.fields.length > 7, // 7 items * 61px = 427px
        })}
      >
        <div className="divide-y divide-border">
          {attendees.fields.map((attendee, index) => (
            <div
              key={attendee.id}
              className="flex items-center justify-between px-3 py-2"
            >
              <div className="flex min-w-0 items-center">
                <Avatar>
                  {/* <AvatarImage src="https://github.com/shadcn.png" /> */}
                  <AvatarFallback className="bg-border capitalize text-muted">
                    {getMonogram(attendee)}
                  </AvatarFallback>
                </Avatar>
                <div className="ml-2 min-w-0">
                  {attendee.isExternal ? (
                    <Badge variant="outline">
                      <span className="flex items-center">
                        <span className="mr-2 inline-block size-2 rounded-full bg-warning" />
                        External
                      </span>
                    </Badge>
                  ) : (
                    <p className="truncate text-sm font-medium text-foreground">
                      {getName(attendee)}{" "}
                      {attendee.isHost && (
                        <Badge variant="outline" className="ml-2">
                          <span className="flex items-center">Host</span>
                        </Badge>
                      )}
                    </p>
                  )}

                  <p className="truncate text-sm text-muted-foreground">
                    {attendee.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!attendee.isExternal && !attendee.isHost && (
                  <FormField
                    control={form.control}
                    name={`guests.${index}.isModerator`}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => attendees.remove(index)}
                  type="button"
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </>
  );
}
